// Pure logic for /chord-caller — a big-screen flashcard practice page: the
// screen calls a chord, the player finds it on a real piano. No judging is
// required (this is a caller, not a scorer), but matchesCard is here for an
// optional "confirm with MIDI" mode. Composes the key/chord tables in
// pianoDrills.js and the wheel + inversion helpers in circleOfFifths.js —
// no music theory is re-derived here, just decks assembled from cards. Same
// no-DOM/no-audio posture as the other music libs.

import {
  keyByName,
  slashLabel,
  parseProgression,
  chordInfo,
  chordLabel,
  NOTE_NAMES_FLAT,
  NOTE_NAMES_SHARP,
} from "./pianoDrills";
import {
  WHEEL,
  INVERSIONS,
  SEVENTH_INVERSIONS,
  triadPcs,
  seventhPcs,
  targetInversionMidis,
  targetSeventhInversionMidis,
  seventhSlashLabel,
  seventhQuality,
} from "./circleOfFifths";

/* ---------- Small shared helpers ---------- */

const pcOf = (midi) => ((midi % 12) + 12) % 12;

const spellPc = (key, pc) => (key.useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP)[pc];

// Rotate a root-position pitch-class list (root, 3rd, 5th[, 7th], ascending
// pitch order) so the `inversion`th tone leads — the bass-first order cards
// display their note names in.
const rotateBassFirst = (rootOrderPcs, inversion) => [
  ...rootOrderPcs.slice(inversion),
  ...rootOrderPcs.slice(0, inversion),
];

const STYLE_TEXT = {
  block: null,
  arpeggio: "arpeggiate — up and down",
  ladder: "run it up the piano — every octave",
};

const buildInstruction = (style, bass, bassName) => {
  const parts = [];
  if (STYLE_TEXT[style]) parts.push(STYLE_TEXT[style]);
  if (bass) parts.push(`left hand: ${bassName} below`);
  return parts.length ? parts.join(" · ") : null;
};

/* ---------- Speech text ---------- */

// A spelled note name ("Eb", "F#") read the way a caller would say it out
// loud — accidentals become words, since a TTS voice either mangles the
// symbol or skips it silently.
const spokenNote = (name) => {
  if (name.endsWith("b")) return `${name.slice(0, -1)} flat`;
  if (name.endsWith("#")) return `${name.slice(0, -1)} sharp`;
  return name;
};

const TRIAD_QUALITY_SPEECH = { maj: "major", min: "minor", dim: "diminished" };

// Keyed by seventhQuality()'s ids (maj7/m7/dom7/halfDim), not display suffixes.
const SEVENTH_QUALITY_SPEECH = {
  maj7: "major seven",
  m7: "minor seven",
  dom7: "seven",
  halfDim: "half diminished",
};

// Spelled-out ordinals for speech — distinct from INVERSIONS/SEVENTH_INVERSIONS'
// display labels ("1st inversion"), which read fine on screen but not aloud.
const POSITION_SPEECH = ["root position", "first inversion", "second inversion", "third inversion"];

// Spoken form of a triad card: "<root> major/minor/diminished", with a
// ", <position>" suffix when withPosition is set (the "inversions" deck).
const triadSpeech = (key, rootPc, degree, inversion, withPosition) => {
  const info = chordInfo(degree);
  const quality = info ? TRIAD_QUALITY_SPEECH[info.quality] : "";
  const base = `${spokenNote(spellPc(key, rootPc))} ${quality}`;
  return withPosition ? `${base}, ${POSITION_SPEECH[inversion]}` : base;
};

// Spoken form of a seventh card: quality-specific phrasing per
// SEVENTH_QUALITY_SPEECH, with the same optional position suffix
// (the "seventhInversions" deck; third inversion only exists here).
const seventhSpeech = (key, rootPc, degree, inversion, withPosition) => {
  const quality = SEVENTH_QUALITY_SPEECH[seventhQuality(degree)] || "";
  const base = `${spokenNote(spellPc(key, rootPc))} ${quality}`;
  return withPosition ? `${base}, ${POSITION_SPEECH[inversion]}` : base;
};

// Voicing window every reference diagram is drawn in — an easy-to-read span
// around middle C, matching the anchor used elsewhere in these libs.
const REF_LOW = 48;
const REF_HIGH = 84;

/* ---------- Card builders ---------- */

// One triad flashcard: keyName's diatonic degree, in the given inversion.
// Returns null if the key/degree combo doesn't resolve (caller filters).
const buildTriadCard = (keyName, degree, inversion, { style, bass, withPosition }, idSuffix) => {
  const key = keyByName(keyName);
  const rootOrderPcs = triadPcs(keyName, degree);
  if (!key || !rootOrderPcs) return null;
  const bassFirstPcs = rotateBassFirst(rootOrderPcs, inversion);
  const noteNames = bassFirstPcs.map((pc) => spellPc(key, pc));
  return {
    id: `t-${keyName}-${degree}-${inversion}-${idSuffix}`,
    title: slashLabel(keyName, degree, inversion),
    subtitle: `${INVERSIONS[inversion].label} · ${keyName} major`,
    instruction: buildInstruction(style, bass, noteNames[0]),
    speech: triadSpeech(key, rootOrderPcs[0], degree, inversion, !!withPosition),
    keyName,
    degree,
    inversion,
    seventh: false,
    midis: targetInversionMidis(keyName, degree, inversion, REF_LOW, REF_HIGH),
    noteNames,
    pcs: [...rootOrderPcs].sort((a, b) => a - b),
    bassPc: bassFirstPcs[0],
  };
};

// One seventh-chord flashcard: mirrors buildTriadCard for four-note chords.
const buildSeventhCard = (keyName, degree, inversion, { style, bass, withPosition }, idSuffix) => {
  const key = keyByName(keyName);
  const rootOrderPcs = seventhPcs(keyName, degree);
  if (!key || !rootOrderPcs) return null;
  const bassFirstPcs = rotateBassFirst(rootOrderPcs, inversion);
  const noteNames = bassFirstPcs.map((pc) => spellPc(key, pc));
  return {
    id: `s-${keyName}-${degree}-${inversion}-${idSuffix}`,
    title: seventhSlashLabel(keyName, degree, inversion),
    subtitle: `${SEVENTH_INVERSIONS[inversion].label} · ${keyName} major`,
    instruction: buildInstruction(style, bass, noteNames[0]),
    speech: seventhSpeech(key, rootOrderPcs[0], degree, inversion, !!withPosition),
    keyName,
    degree,
    inversion,
    seventh: true,
    midis: targetSeventhInversionMidis(keyName, degree, inversion, REF_LOW, REF_HIGH),
    noteNames,
    pcs: [...rootOrderPcs].sort((a, b) => a - b),
    bassPc: bassFirstPcs[0],
  };
};

/* ---------- Deck config ---------- */

const CONTENT_TYPES = [
  "chords",
  "inversions",
  "sevenths",
  "seventhInversions",
  "progression",
  "exercise21",
];
const STYLES = ["block", "arpeggio", "ladder"];
const ORDERS = ["inOrder", "shuffle"];
const DEFAULT_DEGREES = [1, 2, 3, 4, 5, 6, 7];

// Degree sequence for the "21 exercise" — diatonic triads falling in thirds
// (1 → 6 → 4 → 2 → 7 → 5 → 3, then it repeats). Paired with inversions
// cycling 0,1,2, 21 cards (lcm(7,3)) walk every triad through every
// inversion while only ever moving one finger between cards.
export const FALLING_THIRDS_DEGREES = [1, 6, 4, 2, 7, 5, 3];

// Hard cap on deck size — big enough for a serious practice session, small
// enough that a runaway config ("circle" keys × seventh inversions is
// 12 × 7 × 4 = 336) fails fast with a friendly message instead of building a
// deck nobody will ever finish.
const MAX_CARDS = 200;

// Fisher-Yates, seeded by `rng` for deterministic tests.
const shuffle = (arr, rng) => {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

// Build a practice deck from a config:
//   keys:            array of major key names, or "circle" for all 12 keys
//                     in WHEEL (circle-of-fifths) order.
//   content:         "chords" | "inversions" | "sevenths" |
//                     "seventhInversions" | "progression" | "exercise21".
//   degrees:         subset of [1..7] — which diatonic degrees to include.
//                     Ignored by "progression" and "exercise21" (each has its
//                     own fixed sequence). Default: all seven.
//   inversions:      subset of [0,1,2] (triads) or [0,1,2,3] (sevenths).
//                     Only used by "inversions"/"seventhInversions".
//                     Default: every inversion for that chord type.
//   progressionText: e.g. "2 5 1" — required when content is "progression".
//   order:           "inOrder" | "shuffle". Shuffling scrambles the whole
//                     card list (not per-key) — with "circle" keys that means
//                     cards from different keys interleave freely, which is
//                     the more useful practice shuffle; use "inOrder" to keep
//                     keys walking the circle in sequence.
//   style:           "block" | "arpeggio" | "ladder" — sets `instruction`.
//   bass:             when true, appends a "left hand: {note} below" line to
//                     `instruction`.
// Returns { cards, error }. `cards` is [] whenever `error` is set.
export const buildDeck = (config, rng = Math.random) => {
  const cfg = config || {};
  const err = (error) => ({ cards: [], error });

  const style = cfg.style || "block";
  if (!STYLES.includes(style)) return err(`Unknown style: "${style}".`);

  const order = cfg.order || "inOrder";
  if (!ORDERS.includes(order)) return err(`Unknown order: "${order}".`);

  const content = cfg.content;
  if (!CONTENT_TYPES.includes(content)) {
    return err(
      content
        ? `Unknown content type: "${content}".`
        : "Pick what to call: chords, inversions, sevenths, seventh inversions, a progression, or the 21 exercise."
    );
  }

  const bass = !!cfg.bass;

  let keyNames;
  if (cfg.keys === "circle") {
    keyNames = WHEEL.map((w) => w.major);
  } else if (Array.isArray(cfg.keys) && cfg.keys.length) {
    keyNames = cfg.keys;
  } else {
    return err("Pick at least one key.");
  }
  for (const name of keyNames) {
    if (!keyByName(name)) return err(`Unknown key: "${name}".`);
  }

  const isSeventh = content === "sevenths" || content === "seventhInversions";
  const maxInversion = isSeventh ? 3 : 2;

  let degrees = DEFAULT_DEGREES;
  if (cfg.degrees !== undefined) {
    if (!Array.isArray(cfg.degrees) || !cfg.degrees.length) {
      return err("Pick at least one chord degree.");
    }
    for (const d of cfg.degrees) {
      if (!Number.isInteger(d) || d < 1 || d > 7) return err(`Unknown scale degree: ${d}.`);
    }
    degrees = cfg.degrees;
  }

  const needsInversions = content === "inversions" || content === "seventhInversions";
  let inversions = [0];
  if (needsInversions) {
    let provided = isSeventh ? [0, 1, 2, 3] : [0, 1, 2];
    if (cfg.inversions !== undefined) {
      if (!Array.isArray(cfg.inversions) || !cfg.inversions.length) {
        return err("Pick at least one inversion.");
      }
      provided = cfg.inversions;
    }
    for (const inv of provided) {
      if (!Number.isInteger(inv) || inv < 0 || inv > maxInversion) {
        return err(`Invalid inversion: ${inv}.`);
      }
    }
    inversions = [...provided].sort((a, b) => a - b);
  }

  let progressionDegrees = null;
  if (content === "progression") {
    const parsed = parseProgression(cfg.progressionText);
    if (parsed.error) return err(parsed.error);
    progressionDegrees = parsed.degrees;
  }

  const opts = { style, bass };
  const cards = [];
  let idx = 0;
  keyNames.forEach((keyName) => {
    if (content === "chords") {
      degrees.forEach((degree) => cards.push(buildTriadCard(keyName, degree, 0, opts, idx++)));
    } else if (content === "sevenths") {
      degrees.forEach((degree) => cards.push(buildSeventhCard(keyName, degree, 0, opts, idx++)));
    } else if (content === "inversions") {
      // Chord-major order: every inversion of one degree before the next
      // degree — Dm, Dm/F, Dm/A, then Em, Em/G, Em/B, ...
      degrees.forEach((degree) => {
        inversions.forEach((inversion) => {
          cards.push(buildTriadCard(keyName, degree, inversion, { ...opts, withPosition: true }, idx++));
        });
      });
    } else if (content === "seventhInversions") {
      degrees.forEach((degree) => {
        inversions.forEach((inversion) => {
          cards.push(buildSeventhCard(keyName, degree, inversion, { ...opts, withPosition: true }, idx++));
        });
      });
    } else if (content === "progression") {
      progressionDegrees.forEach((degree) => {
        cards.push(buildTriadCard(keyName, degree, 0, opts, idx++));
      });
    } else if (content === "exercise21") {
      // Falling thirds through every triad, inversions cycling 0,1,2 — 21
      // cards (lcm(7,3)) wraps seamlessly, and cfg.degrees/cfg.inversions are
      // ignored, same as "progression" ignores degrees.
      for (let i = 0; i < 21; i += 1) {
        const degree = FALLING_THIRDS_DEGREES[i % 7];
        const inversion = i % 3;
        cards.push(buildTriadCard(keyName, degree, inversion, { ...opts, withPosition: true }, idx++));
      }
    }
  });

  const validCards = cards.filter(Boolean);
  if (!validCards.length) return err("This deck has no cards — widen your selection.");
  if (validCards.length > MAX_CARDS) {
    return err(
      `That's ${validCards.length} cards — too many for one deck (max ${MAX_CARDS}). ` +
        "Try fewer keys or inversions."
    );
  }

  return { cards: order === "shuffle" ? shuffle(validCards, rng) : validCards, error: null };
};

/* ---------- Played-note matching (optional MIDI mode) ---------- */

// Does a set of played midis match the card's chord? Pitch-class set
// equality — any octave, any doubling. When `strictBass` is true, the
// lowest played note's pitch class must also equal card.bassPc.
export const matchesCard = (playedMidis, card, strictBass = false) => {
  if (!card || !playedMidis || !playedMidis.length) return false;
  const played = new Set(playedMidis.map(pcOf));
  const want = card.pcs;
  if (played.size !== want.length) return false;
  for (const pc of want) if (!played.has(pc)) return false;
  if (strictBass && pcOf(Math.min(...playedMidis)) !== card.bassPc) return false;
  return true;
};

/* ---------- Presets ---------- */

// Presets with `keyScoped: true` are written against one concrete key and can
// be re-rooted anywhere via presetConfigForKey; their blurbs template the key
// name as "{key}" (render them through presetBlurb so a blurb never claims C
// while building Eb). The "circle" presets ignore key choice by design.
export const PRESET_DECKS = [
  {
    id: "warmup",
    label: "Key warm-up",
    keyScoped: true,
    blurb: "Every diatonic chord in {key}, straight through.",
    config: { keys: ["C"], content: "chords", order: "inOrder", style: "block" },
  },
  {
    id: "ladder",
    label: "Inversion ladder",
    keyScoped: true,
    blurb: "Every inversion of every chord in {key} — run each one up the keyboard.",
    config: { keys: ["C"], content: "inversions", order: "inOrder", style: "ladder" },
  },
  {
    id: "exercise-21",
    label: "21 Exercise",
    keyScoped: true,
    blurb:
      "Every diatonic chord in every inversion, one finger at a time — move the top note up a " +
      "step, then the middle, then the bottom. The chords fall in thirds: {thirds}, around and " +
      "around.",
    hint: "Only one note moves. If your hand jumped, you left the exercise.",
    config: { keys: ["C"], content: "exercise21", order: "inOrder", style: "block" },
  },
  {
    id: "circle-tour",
    label: "Around the circle",
    blurb: "The tonic triad and its inversions, once around the circle of fifths.",
    config: {
      keys: "circle",
      content: "inversions",
      degrees: [1],
      inversions: [0, 1, 2],
      order: "inOrder",
      style: "block",
    },
  },
  {
    id: "two-five",
    label: "ii–V–I everywhere",
    blurb: "The ii–V–I progression, once around the circle of fifths.",
    config: {
      keys: "circle",
      content: "progression",
      progressionText: "2 5 1",
      order: "inOrder",
      style: "block",
    },
  },
  {
    id: "pop",
    label: "Pop loop",
    keyScoped: true,
    blurb: "The I–V–vi–IV loop in {key}.",
    config: {
      keys: ["C"],
      content: "progression",
      progressionText: "1 5 6 4",
      order: "inOrder",
      style: "block",
    },
  },
  {
    id: "sevenths",
    label: "Sevenths tour",
    keyScoped: true,
    blurb: "Seventh chords on I, ii, and V in {key} — every inversion.",
    config: {
      keys: ["C"],
      content: "seventhInversions",
      degrees: [1, 2, 5],
      order: "inOrder",
      style: "block",
    },
  },
];

// Re-root a key-scoped preset's config to `keyName`. Circle presets (and an
// unknown key name) come back unchanged — buildDeck stays the validator of
// record either way.
export const presetConfigForKey = (preset, keyName) =>
  preset && preset.keyScoped && keyByName(keyName)
    ? { ...preset.config, keys: [keyName] }
    : preset.config;

// A preset's blurb with the "{key}" and "{thirds}" tokens filled in. Safe on
// presets that don't use one or the other (nothing to replace). "{thirds}"
// becomes the falling-thirds chord chain for keyName, e.g. in C:
// "C → Am → F → Dm → B° → G → Em".
export const presetBlurb = (preset, keyName) =>
  preset.blurb
    .replace("{key}", keyName)
    .replace(
      "{thirds}",
      FALLING_THIRDS_DEGREES.map((degree) => chordLabel(keyName, degree)).join(" → ")
    );

/* ---------- Saved decks (localStorage) ---------- */

// Plenty of room for a serious collection without the picker scrolling
// forever.
export const MAX_SAVED_DECKS = 30;

const MAX_SAVED_DECK_NAME = 60;

const isPlainObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);

// Parse the raw localStorage string for saved decks into a clean list.
// Tolerant of anything: missing key (null), corrupt JSON, a non-array
// payload, or entries that are missing fields, badly typed, or whose config
// no longer builds a valid deck (e.g. a key/preset that got removed) — those
// are silently dropped rather than failing the whole list. Trims/caps names
// and caps the list length so a hand-edited or long-lived blob can't blow up
// the UI.
export function parseSavedDecks(raw) {
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const out = [];
  for (const entry of parsed) {
    if (out.length >= MAX_SAVED_DECKS) break;
    if (!isPlainObject(entry)) continue;
    if (typeof entry.id !== "string" || !entry.id.trim()) continue;
    if (typeof entry.name !== "string") continue;
    const name = entry.name.trim().slice(0, MAX_SAVED_DECK_NAME);
    if (!name) continue;
    if (!isPlainObject(entry.config)) continue;
    const { cards, error } = buildDeck(entry.config);
    if (error || !cards.length) continue;
    out.push({ id: entry.id, name, config: entry.config });
  }
  return out;
}
